import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import axios from "axios";

import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUserProfile } from "../../context/UserProfileContext";
import { useUser } from "../../hooks/Authorize";
import StorageFullPopupDialog from "./StorageFullPopupDialog";

const CreateFolderDialog = ({
  open,
  onClose,
  clikkleItems,
  setClikkleItems,
  setShowProcessingSnackbar,
  setProcessingMessage,
}) => {
  const [folderName, setFolderName] = useState("");
  const [error, setError] = useState("");
  const platformUser = JSON.parse(localStorage.getItem("user"));
  const location = useLocation();

  var isViewerScreen = location.pathname === "/view";

  const { fsUserProfile, setFsUserProfile } = useUserProfile();
  const [userTotalSpacePlan, setUserTotalSpacePlan] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);

  const [isSpaceFull100ModalOpen, setIsSpaceFull100ModalOpen] = useState(false);

  const getFsUserProfile = useCallback(async () => {
    try {
      setIsLoadingProfile(true);
      const response = await axios.get(
        `${process.env.REACT_APP_NODE_FILE_MANAGER_SERVER}/api/user/${platformUser._id}`
      );
      setFsUserProfile(response.data);
      console.log("setFsUserProfile = ", response.data);
    } catch (error) {
      console.error("Profile Fetch Error:", error);
    } finally {
      setIsLoadingProfile(false);
    }
  }, [platformUser._id, setFsUserProfile]);

  const checkSubscriptionDetails = useCallback(async () => {
    try {
      setIsLoadingSubscription(true);
      const response = await axios.get(
        `${process.env.REACT_APP_NODE_FILE_MANAGER_SERVER}/api/subscription/${platformUser._id}`
      );

      const data = response.data;
      setUserTotalSpacePlan(data?.permissions);
    } catch (error) {
      console.log("API Error:", error?.response?.data);
    } finally {
      setIsLoadingSubscription(false);
    }
  }, [platformUser._id]);

  useEffect(() => {
    getFsUserProfile();
    checkSubscriptionDetails();
  }, [getFsUserProfile, checkSubscriptionDetails]);

  // Storage calculation logic
  const BYTES_IN_GB = 1073741824;
  const BYTES_IN_MB = 1048576;

  const noOfAllowed = userTotalSpacePlan?.noOfAllowed ?? 5;
  const format = userTotalSpacePlan?.format ?? "GB";
  const isUnlimited = userTotalSpacePlan?.isUnlimited ?? false;

  let TOTAL_SPACE_GB = 0;
  let percentageUsed = 0;
  let isSpaceFull100 = false;

  if (!isUnlimited) {
    if (format === "TB") {
      TOTAL_SPACE_GB = noOfAllowed * 1024;
      const usedBytes = fsUserProfile?.usedSpace ?? 0;
      const usedGB = usedBytes / BYTES_IN_GB;
      percentageUsed = (usedGB / TOTAL_SPACE_GB) * 100;
    } else if (format === "GB") {
      TOTAL_SPACE_GB = noOfAllowed;
      const usedBytes = fsUserProfile?.usedSpace ?? 0;
      const usedGB = usedBytes / BYTES_IN_GB;
      percentageUsed = (usedGB / TOTAL_SPACE_GB) * 100;
    } else if (format === "MB") {
      TOTAL_SPACE_GB = noOfAllowed;
      const usedBytes = fsUserProfile?.usedSpace ?? 0;
      const usedMB = usedBytes / BYTES_IN_MB;
      percentageUsed = (usedMB / TOTAL_SPACE_GB) * 100;
    }

    isSpaceFull100 = percentageUsed >= 90;
  }

  const handleCreateFolder = async () => {
    if (isSpaceFull100) {
      setIsSpaceFull100ModalOpen(true);
      return;
    }
    if (!folderName.trim()) return;

    const formData = new FormData();
    formData.append("ownerId", platformUser._id);
    formData.append("type", "folder");
    formData.append("name", folderName);
    formData.append("ownerEmail", platformUser.email);
    const searchParams = new URLSearchParams(window.location.search);
    const parentId = searchParams.get("parentId");
    if (parentId && !isViewerScreen) {
      formData.append("parentId", parentId);
    }
    try {
      setShowProcessingSnackbar(true);
      setProcessingMessage("Creating folder...");
      const response = await axios.post(
        `${process.env.REACT_APP_NODE_FILE_MANAGER_SERVER}/api/fs/create?id=${platformUser._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (!isViewerScreen) {
        setClikkleItems([...clikkleItems, response.data.record]);
      }

      onClose();
      setFolderName("");
    } catch (error) {
      console.error("Folder creation failed:", error);
      setError("Failed to create folder.");
    }
    setShowProcessingSnackbar(false);
  };

  const handleKeyUp = (e) => {
    const name = e.target.value;
    setFolderName(name);

    if (
      clikkleItems.some((item) => item.name === name && item.type === "folder")
    ) {
      setError("Folder name already exists.");
    } else {
      setError("");
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            backgroundColor: "#191919",
            background: "rgba(31, 31, 31, 1)",
            color: "#fff",
            borderRadius: "12px",
            padding: "2px",
            width: "600px",
            height: "210px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Create Folder
          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ paddingBottom: "0px", maxHeight: "75px" }}>
          <TextField
            placeholder="Enter folder name"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            onKeyUp={handleKeyUp}
            error={Boolean(error)}
            helperText={error}
            sx={{ marginBottom: "0px", height: "40px" }}
            fullWidth
            variant="outlined"
            InputProps={{
              sx: {
                backgroundColor: "#222",
                borderRadius: "8px",
                color: "#fff",
                fontSize: "14px",
                border: "1px solid rgba(41, 41, 41, 1)",
                "&:hover": {
                  border: "1px solid rgba(255, 255, 255, 0.4)",
                },
                "&.Mui-focused": {
                  border: "1px solid rgba(255, 255, 255, 0.6)",
                },
                "& .MuiInputBase-input": {
                  padding: "8px 14px",
                  color: "rgb(86, 86, 86)",
                },
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", padding: "0px 16px" }}>
          <Button
            onClick={onClose}
            sx={{
              color: "#fff",
              textTransform: "none",
              fontSize: "14px",
              padding: "6px 12px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateFolder}
            disabled={!folderName.trim() || Boolean(error)}
            sx={{
              backgroundColor: "rgba(55, 103, 177, 1)",
              color: "#fff",
              borderRadius: "8px",
              padding: "6px 16px",
              textTransform: "none",
              fontSize: "14px",
              "&:hover": {
                backgroundColor: "#1750B6",
              },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Only show popup if storage is actually 100% full */}
      {isSpaceFull100 && (
        <StorageFullPopupDialog
          isSpaceFull100ModalOpen={isSpaceFull100ModalOpen}
          isSpaceFull100={isSpaceFull100}
          is100Full={isSpaceFull100}
          setIsSpaceFull100ModalOpen={setIsSpaceFull100ModalOpen}
          isUnlimited={isUnlimited}
          dialogLoading={isLoadingProfile || isLoadingSubscription}
          noOfAllowed={noOfAllowed}
          format={format}
        />
      )}
    </>
  );
};

export default CreateFolderDialog;